import React, { Component } from "react";
import "./WaveformDisplay.css";
import WaveformDisplayCache from "./WaveformDisplayCache";

/**
 * @typedef {Object} WaveformDisplayProps
 * @property {string} trackId - The track ID
 * @property {AudioBuffer | null} audioBuffer - The audio buffer for waveform rendering
 * @property {number | null} renderWidth - The width to render the waveform
 * @property {WaveformDisplayCache} cache - The waveform display cache
 */

class WaveformDisplay extends Component {
  /** @type {HTMLDivElement | null} */
  containerRef;

  /** @type {HTMLCanvasElement | null} */
  canvasRef;

  /**
   * @param {WaveformDisplayProps} props
   */
  constructor(props) {
    super(props);
    props.cache.recomputeSavedBuffer(
      props.audioBuffer,
      props.trackId,
      this.rerenderWave
    );
  }

  componentDidMount() {
    window.addEventListener("resize", this.windowResizeListener);
  }

  componentWillUnmount() {
    // console.log(
    //   "Unmounting a WaveformDisplay, and removing the resize listener"
    // );
    window.removeEventListener("resize", this.windowResizeListener);
  }

  windowResizeListener = () => {
    this.refreshResolution();
  };

  refreshResolution = () => {
    if (this.containerRef != null && this.props.audioBuffer != null) {
      const oldResolution = this.props.cache.savedBuffer.length;
      const width = this.containerRef.getBoundingClientRect().width;
      this.props.cache.requestResolution(width * 2, this.props.audioBuffer);
      const newResolution = this.props.cache.savedBuffer.length;
      if (newResolution > oldResolution) {
        this.rerenderWave();
      }
    }
  };

  /**
   * @param {HTMLDivElement | null} ref
   */
  setContainerRef = (ref) => {
    if (ref != null) {
      this.containerRef = ref;
    }
  };

  /**
   * @param {HTMLCanvasElement | null} ref
   */
  setCanvasRef = (ref) => {
    this.canvasRef = ref;
    if (this.props.audioBuffer != null && this.containerRef != null) {
      const width = this.containerRef.getBoundingClientRect().width;
      this.props.cache.requestResolution(width * 2, this.props.audioBuffer);
    }
    this.rerenderWave();
  };

  rerenderWave = () => {
    /** @type {Array<number> | null} */
    const savedBuffer = this.props.cache.savedBuffer;
    if (
      savedBuffer != null &&
      savedBuffer.length > 0 &&
      this.canvasRef != null
    ) {
      const canvasRef = this.canvasRef;
      const context = canvasRef.getContext("2d");
      const width =
        this.props.renderWidth != null
          ? this.props.renderWidth
          : canvasRef.getBoundingClientRect().width;
      const height = canvasRef.getBoundingClientRect().height;

      context.save();

      context.canvas.width = width;
      context.canvas.height = height;
      context.globalAlpha = 1.0;
      context.clearRect(0, 0, width, height);
      // context.strokeStyle = "#979797";
      context.strokeStyle = "#272727";
      context.translate(0, height / 2);

      const bufferPerPixel = savedBuffer.length / width;
      const wave = [];
      let max = 0;
      for (let x = 0; x < width; x += 1) {
        // on which line do we get ?
        const i = Math.floor(x * bufferPerPixel);

        const AVG_OVER_SAMPLES = 2;

        const avgFrom = Math.max(0, i - AVG_OVER_SAMPLES);
        let sum = 0;
        for (let j = avgFrom; j < i; j += 1) {
          sum += Math.abs(savedBuffer[j]);
        }
        sum /= AVG_OVER_SAMPLES;
        wave.push(sum);
        max = Math.max(sum, max);
      }

      for (let x = 0; x < width; x += 1) {
        const sum = wave[x] / max;
        const y = (sum * height * 0.85) / 2;
        context.beginPath();
        context.moveTo(x, -y);
        context.lineTo(x, y);
        context.stroke();
      }

      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(width, 0);
      context.stroke();

      context.restore();
    }
  };

  render() {
    return (
      <div className={"WaveformDisplay"} ref={this.setContainerRef}>
        <canvas ref={this.setCanvasRef} className={"WaveformDisplay-canvas"} />
      </div>
    );
  }
}

export default WaveformDisplay;
